import { createContext } from 'react';

import type { StepUpAction } from '../api/types';

/** Thrown when the user dismisses the step-up dialog instead of confirming. */
export class StepUpCancelledError extends Error {
  constructor() {
    super('step-up cancelled');
    this.name = 'StepUpCancelledError';
  }
}

export function isStepUpCancelled(error: unknown): error is StepUpCancelledError {
  return error instanceof StepUpCancelledError;
}

export interface StepUpContextValue {
  /**
   * Prompts for a TOTP code and resolves with a step-up token bound to `action`.
   * Rejects with `StepUpCancelledError` if the user backs out.
   */
  requestStepUp: (action: StepUpAction) => Promise<string>;
  /** Drops every cached token. Called on sign-out. */
  reset: () => void;
}

export const StepUpContext = createContext<StepUpContextValue | null>(null);

/**
 * A step-up token is valid for five minutes, but it is only reused inside a much
 * smaller window. The gap absorbs clock skew and the round-trip, and — more to
 * the point — keeps the reuse window short enough that "I confirmed a payout a
 * few minutes ago" cannot silently authorise a second one the user never saw.
 */
export const STEP_UP_REUSE_SKEW_MS = 30_000;
